<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';

import type {
  PartnerCampaignFilterOption,
  PartnerViewFilters as PartnerViewFiltersType,
  PartnerViewItem,
} from '../types';

import PartnerViewFilters from '../components/PartnerViewFilters.vue';
import PartnerViewTable from '../components/PartnerViewTable.vue';
import { DEFAULT_PAGE_SIZE } from '../constants';
import {
  extractCampaignFilterOptionsFromPartners,
  mapTargetListResponse,
  mergeCampaignFilterOptions,
  parsePartnerSearchParams,
} from '../mappers/partnerMappers';

const router = useRouter();

const isLoading = ref(true);
const hasError = ref(false);
const partners = ref<PartnerViewItem[]>([]);
const campaignOptions = ref<PartnerCampaignFilterOption[]>([]);
const filters = ref<PartnerViewFiltersType>({ campaignId: '', search: '' });
const currentPage = ref(1);
const pageSize = ref(DEFAULT_PAGE_SIZE);
const totalCount = ref(0);
const totalPages = ref(0);

const hasNext = computed(() => currentPage.value < totalPages.value);

async function loadPartners() {
  isLoading.value = true;
  hasError.value = false;

  const searchParams = parsePartnerSearchParams(filters.value.search);

  try {
    const data = await campaignManagementService.listTargets({
      page: currentPage.value,
      page_size: pageSize.value,
      ...(filters.value.campaignId ? { campaign_id: filters.value.campaignId } : {}),
      ...searchParams,
    });

    const mapped = mapTargetListResponse(data);
    partners.value = mapped.partners;
    totalCount.value = mapped.total;
    totalPages.value = mapped.pages;
    currentPage.value = mapped.page;
    campaignOptions.value = mergeCampaignFilterOptions(
      campaignOptions.value,
      extractCampaignFilterOptionsFromPartners(mapped.partners),
    );
  } catch {
    hasError.value = true;
    toast.error('Não foi possível carregar os parceiros. Tente novamente.');
  } finally {
    isLoading.value = false;
  }
}

function reloadFromFirstPage() {
  if (currentPage.value === 1) {
    loadPartners();
  } else {
    currentPage.value = 1;
  }
}

function onApplyFilters(newFilters: PartnerViewFiltersType) {
  filters.value = newFilters;
  reloadFromFirstPage();
}

function onClearFilters() {
  filters.value = { campaignId: '', search: '' };
  reloadFromFirstPage();
}

function onPageChange(page: number) {
  currentPage.value = page;
}

function onPageSizeChange(size: number) {
  pageSize.value = size;
  reloadFromFirstPage();
}

function onViewPartner(partner: PartnerViewItem) {
  router.push(`/revalida-partner-view/${partner.id}`);
}

watch(currentPage, () => {
  loadPartners();
}, { immediate: true });

onMounted(() => {
  document.title = 'Visão por Parceiro';
});
</script>

<template>
  <DashboardLayout
    title="Revalida Instalação"
    description="Visão por Parceiro"
    subtitle="Veja qual campanha cada parceiro está participando hoje, o histórico de jornadas
      e quantas instalações foram revalidadas."
  >
    <div class="row row-cards">
      <PartnerViewFilters
        :campaign-options="campaignOptions"
        @apply="onApplyFilters"
        @clear="onClearFilters"
      />

      <PartnerViewTable
        :rows="partners"
        :is-loading="isLoading"
        :has-error="hasError"
        :current-page="currentPage"
        :has-next="hasNext"
        :page-size="pageSize"
        :total-count="totalCount"
        @page-change="onPageChange"
        @page-size-change="onPageSizeChange"
        @view="onViewPartner"
      />
    </div>
  </DashboardLayout>
</template>
